import * as React from "react"
import { formatMoney } from "./util/format"
let styles = require("./styles.module.scss")
import cx from "classnames"
import TeslaRange from "./range"
import TeslaToggleRadio from "./toggle-radio"
import TeslaToggleAssumptions from "./toggle-assumptions"
import { PaymentOptions } from "global"
import Button from "components/Button"

export type TeslaRoofCalculatorProps = {
  name?: string
}

export type TeslaRoofCalculatorState = {
  debug?: boolean
  roofSqFt: number
  roofCoverage: number
  roofDisplaySize: {
    width: number
    height: number
  }
  paymentOption: PaymentOptions
  isAssumptionsOpen: boolean
  cashLoanTerm: number
  cashState?: number
}

export default class RoofCalculator extends React.Component<
  TeslaRoofCalculatorProps,
  TeslaRoofCalculatorState
> {
  roof: HTMLElement
  financingAssumptionsContent: HTMLElement
  cashAssumptionsContent: HTMLElement
  constants: any // TOOD - type this

  constructor(props) {
    super(props)

    this.state = {
      debug: false,
      roofSqFt: 1500,
      roofCoverage: 50,
      roofDisplaySize: {
        width: 50,
        height: 40,
      },
      paymentOption: "financing",
      isAssumptionsOpen: false,
      cashLoanTerm: 30,
    }

    this.constants = {
      roofDisplay: {
        width: 50,
        height: 40,
      },
      sliders: {
        roofSizeMin: 500,
        roofSizeMax: 5000,
        roofCoverageMin: 20,
        roofCoverageMax: 70,
      },
      costs: {
        perSqFtRoof: 11,
        perSqFtSolar: 33,
        installationFee: 0,
        kwhPrice: 0.1739,
      },
      energy: {
        kwhConsumptionPerMonth: 300,
        kwhProducedBySqFtTilePerYear: 7.75,
      },
      roofSqFt: 1000,
      roofCoverage: 50,
      roofDisplayMinimumSize: {
        width: 50,
        height: 40,
      },
      roofDisplaySize: {
        width: 50,
        height: 40,
      },
      retailFinancing: {
        rate: 0.12,
        term: 5,
      },
      betterFinancing: {
        rate: 0.04,
        term: 30,
      },
      borrower: {
        ltv: 0.6,
        homeValue: 816480,
        rate: 0.04125,
        term: 30,
      },
      better: {
        rate: 0.04375,
        term: 30,
      },
      retail: {
        rate: 0.12,
        term: 5,
        max: 35000,
      },
      taxes: {
        solarTaxCreditRate: 0.3,
      },
    }
  }

  // Electricity
  getAverage30YearKwhPriceWithInflation() {
    // Assumes 2% inflation
    return this.constants.costs.kwhPrice * Math.pow(1.02, 15)
  }
  getSolarRoofElectricityProductionPerYear() {
    return (
      this.constants.energy.kwhProducedBySqFtTilePerYear *
      this.getTotalSqFtSolarTile()
    )
  }
  getAverageMonthlyElectricityIncome() {
    return (
      (this.getAverage30YearKwhPriceWithInflation() *
        this.getSolarRoofElectricityProductionPerYear()) /
      12
    )
  }

  // Utility
  getTotalSqFtSolarTile() {
    return this.state.roofSqFt * (this.state.roofCoverage / 100)
  }
  getMonthlyPayment(rate, term, total, debugName) {
    const preTaxPayment = this.PMT(rate / 12, term * 12, total, 0, 0).toFixed(2)
    const period = 1 // First month
    const interest = this.IPMT(rate / 12, period, total, preTaxPayment)

    if (this.state.debug) {
      console.log(`${debugName} Pretax: $${preTaxPayment}`)
      console.log(`${debugName} interest: $${interest}`)
    }

    // 35% Mortgage Interest Tax Deduction
    return preTaxPayment - 0.35 * interest
  }

  // Roof Costs
  getCostOfSolarPanels() {
    return this.constants.costs.perSqFtSolar * this.getTotalSqFtSolarTile()
  }
  getCostOfRoofInstallation() {
    return this.constants.costs.perSqFtRoof * this.state.roofSqFt
  }
  getCostOfRoof() {
    return this.getCostOfSolarPanels()
  }

  // Borrower
  getOldMortgageTotal() {
    return this.constants.borrower.ltv * this.constants.borrower.homeValue
  }
  getOldMonthlyPayment() {
    return this.getMonthlyPayment(
      this.constants.borrower.rate,
      this.constants.borrower.term,
      this.getOldMortgageTotal(),
      "Old"
    )
  }
  getSolarMortgageTotal() {
    return this.getOldMortgageTotal() + this.getCostOfRoof()
  }
  getSolarMonthlyPayment() {
    return this.getMonthlyPayment(
      this.constants.better.rate,
      this.constants.better.term,
      this.getSolarMortgageTotal(),
      "Solar"
    )
  }
  getSolarMonthlyDifference() {
    return this.getSolarMonthlyPayment() - this.getOldMonthlyPayment()
  }
  getRetailMonthlyPayment() {
    return this.getMonthlyPayment(
      this.constants.retail.rate,
      this.constants.retail.term,
      this.getCostOfRoof(),
      "Retail"
    )
  }

  // Savings
  // line 54, 55
  getMonthlyNetCost(firstYear = false) {
    const credit = !firstYear
      ? 0
      : (this.constants.taxes.solarTaxCreditRate * this.getCostOfRoof()) / 12 // there is a monthly credit in first year
    return (
      -this.getSolarMonthlyDifference() -
      this.getAverageMonthlyElectricityIncome() -
      credit
    )
  }
  // line 59
  getTotalCostToConsumer() {
    return (
      this.getMonthlyNetCost(true) * 12 +
      this.getMonthlyNetCost() * 12 * (this.state.cashLoanTerm - 1)
    )
  }
  getValueOfSolarTaxCreditOverFullTerm(term) {
    return (
      (this.constants.taxes.solarTaxCreditRate * this.getCostOfRoof()) / term
    )
  }
  getSolarTotalNetCost() {
    return (
      -this.getSolarMonthlyDifference() -
      this.getAverageMonthlyElectricityIncome() -
      this.getValueOfSolarTaxCreditOverFullTerm(this.state.cashLoanTerm * 12)
    )
  }
  getRetailTotalNetCost() {
    return (
      -this.getRetailMonthlyPayment() -
      this.getAverageMonthlyElectricityIncome() -
      this.getValueOfSolarTaxCreditOverFullTerm(5 * 12)
    )
  }
  getSolarLifetimeCostToConsumer() {
    return this.getSolarTotalNetCost() * this.state.cashLoanTerm * 12
  }

  // Market Gains
  // Line 62
  getAnnualizedReturnsSP500ForRoofCost() {
    return this.getCostOfRoof() * 0.1 * 0.65
  }

  getTotalReturnsSP500ForRoofCost() {
    return this.getAnnualizedReturnsSP500ForRoofCost() * this.state.cashLoanTerm
  }

  getOppurtunityCostToConsumer() {
    return (
      this.getTotalCostToConsumer() - this.getTotalReturnsSP500ForRoofCost()
    )
  }

  // Lifted from Fil's SavingsCalculator
  PMT(ir, np, pv, fv = 0, type = 0) {
    /*
     * ir   - interest rate per month
     * np   - number of periods (months)
     * pv   - present value
     * fv   - future value
     * type - when the payments are due:
     *        0: end of the period, e.g. end of month (default)
     *        1: beginning of period
     */
    let pmt
    let pvif
    if (ir === 0) {
      return -(pv + fv) / np
    }

    pvif = Math.pow(1 + ir, np)
    pmt = (-ir * pv * (pvif + fv)) / (pvif - 1)

    if (type === 1) {
      pmt /= 1 + ir
    }

    return pmt
  }

  // Lifted from https://gist.github.com/pies/4166888
  IPMT(ir, per, pv, pmt) {
    const tmp = Math.pow(1 + ir, per)
    return 0 - (pv * tmp * ir + pmt * (tmp - 1))
  }

  updateRoofSizeRange(value) {
    this.setState({ roofSqFt: value })
    this.updateRoofDisplaySize()
  }

  updateRoofCoverageRange(value) {
    this.setState({ roofCoverage: value })
  }

  updateRoofDisplaySize() {
    const roofSizeUnit = Math.floor((this.state.roofSqFt - 1000) / 400)
    const roofDisplaySize = {
      width: this.constants.roofDisplay.width + roofSizeUnit,
      height: this.constants.roofDisplay.height + roofSizeUnit,
    }

    this.setState({ roofDisplaySize })
  }

  setPaymentOption(value) {
    this.setState({ paymentOption: value })
    this.setAssumptionsContentMaxHeight()
  }

  setLoanState(value) {
    this.setState({ cashState: value })
  }

  setLoanTerm(value) {
    this.setState({ cashLoanTerm: value })
  }

  rotateRoof() {
    let percentage = 0

    if (this.roof.getBoundingClientRect().bottom < 0) {
      percentage = 0
      this.roof.style.transform = `rotateX(${55 + percentage / 4}deg)`
    } else if (this.roof.getBoundingClientRect().top > window.innerHeight) {
      percentage = 100
      this.roof.style.transform = `rotateX(${55 + percentage / 4}deg)`
    } else {
      percentage = Math.round(
        (this.roof.getBoundingClientRect().top / window.innerHeight) * 100
      )
      this.roof.style.transform = `rotateX(${55 + percentage / 4}deg)`
    }
  }

  toggleAssumptions() {
    this.setState({
      isAssumptionsOpen: !this.state.isAssumptionsOpen,
    })
    this.setAssumptionsContentMaxHeight()
  }

  setAssumptionsContentMaxHeight() {
    switch (this.state.paymentOption) {
      case "cash":
        this.cashAssumptionsContent.style.maxHeight = `${
          this.cashAssumptionsContent.firstElementChild.getBoundingClientRect()
            .height
        }px`
        break
      case "financing":
        this.financingAssumptionsContent.style.maxHeight = `${
          this.financingAssumptionsContent.firstElementChild.getBoundingClientRect()
            .height
        }px`
        break
    }
  }

  componentDidMount() {
    window.addEventListener(
      "scroll",
      (this.rotateRoof = this.rotateRoof.bind(this))
    )
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.rotateRoof)
  }

  render() {
    return (
      <section className={styles.calculator}>
        <div className={styles.pagePadding}>
          <div className={styles.header}>
            <h2 className={styles.heading}>
              The larger your Solar Roof, the more you save
            </h2>
          </div>
          {/* sliders */}
          <div className={styles.sliders}>
            <div className={styles.roofSizeSlider}>
              <div className={styles.range}>
                <h5 className={styles.sliderLabel}>Roof Size</h5>
                <TeslaRange
                  className={styles.sliders}
                  value={this.state.roofSqFt}
                  min={this.constants.sliders.roofSizeMin}
                  max={this.constants.sliders.roofSizeMax}
                  step={10}
                  updateFn={this.updateRoofSizeRange.bind(this)}
                />
              </div>
            </div>
            <div className={styles.roofCoverageSlider}>
              <div className={styles.range}>
                <h5 className={styles.sliderLabel}>Roof Coverage</h5>
                <TeslaRange
                  className={styles.sliders}
                  value={this.state.roofCoverage}
                  min={this.constants.sliders.roofCoverageMin}
                  max={this.constants.sliders.roofCoverageMax}
                  step={1}
                  tip={"%"}
                  updateFn={this.updateRoofCoverageRange.bind(this)}
                />
              </div>
            </div>
          </div>
          {/* roof */}
          <div className={styles.roofWrapper}>
            <div
              className={styles.roof}
              ref={(element) => {
                this.roof = element
              }}
            >
              <div
                className={styles.roofSize}
                style={{
                  width: `${this.state.roofDisplaySize.width}vw`,
                  height: `${this.state.roofDisplaySize.height}vh`,
                }}
              >
                <div
                  className={styles.roofShadow}
                  style={{ width: `${this.state.roofCoverage * 0.8}%` }}
                />
                <div className={styles.nonSolarBorder} />
                <div className={styles.nonSolar} />
                <div
                  className={styles.normalTiles}
                  style={{ width: `${this.state.roofCoverage}%` }}
                />
                <div
                  className={styles.solarTiles}
                  style={{ width: `${this.state.roofCoverage}%` }}
                />
              </div>
            </div>
          </div>
          {/* cost breakdown */}
          <div
            className={styles.option}
            data-js-calculator-option={this.state.paymentOption}
          >
            {/* toggle */}
            <TeslaToggleRadio
              labels={["Financing", "Paying Cash"]}
              updateFn={this.setPaymentOption.bind(this)}
              teslaToggle={true}
            />

            <div
              className={cx({
                [styles.debug]: true,
                [styles.debugOn]: this.state.debug,
              })}
            >
              <h4> Electricity </h4>
              <div>
                {" "}
                Energy Production per Year:{" "}
                {this.getSolarRoofElectricityProductionPerYear().toFixed(
                  2
                )} kWh{" "}
              </div>
              <div>
                {" "}
                Average 30 year inflation $
                {this.getAverage30YearKwhPriceWithInflation().toFixed(2)}{" "}
              </div>
              <div>
                {" "}
                Avg Monthly Electricity Income $
                {this.getAverageMonthlyElectricityIncome().toFixed(2)}{" "}
              </div>

              <h4> Borrower </h4>
              <div> Roof Cost: ${this.getCostOfRoof()} </div>
              <div> Old Mortgage Total: ${this.getOldMortgageTotal()} </div>
              <div>
                {" "}
                Old Monthly Payment: ${
                  -this.getOldMonthlyPayment().toFixed(2)
                }{" "}
              </div>
              <div> Solar Mortgage Total: ${this.getSolarMortgageTotal()} </div>
              <div>
                {" "}
                Solar Monthly Payment: $
                {-this.getSolarMonthlyPayment().toFixed(2)}{" "}
              </div>
              <div>
                {" "}
                Net Difference: ${
                  -this.getSolarMonthlyDifference().toFixed(2)
                }{" "}
              </div>
              <div>
                {" "}
                Retail Monthly Payment: $
                {-this.getRetailMonthlyPayment().toFixed(2)}{" "}
              </div>

              <h4> Savings </h4>
              <div>
                {" "}
                Tax Credits per month: $
                {(this.constants.taxes.solarTaxCreditRate *
                  this.getCostOfRoof()) /
                  12}{" "}
              </div>
              <div>
                {" "}
                Monthly Net Cost In Year 1 (includes Solar Tax Credit): $
                {this.getMonthlyNetCost(true).toFixed(2)}{" "}
              </div>
              <div>
                {" "}
                Monthly Net Cost After Year 1: $
                {this.getMonthlyNetCost().toFixed(2)}{" "}
              </div>
              <div>
                {" "}
                Year Net Cost In Year 1 (includes Solar Tax Credit): $
                {(this.getMonthlyNetCost(true) * 12).toFixed(0)}{" "}
              </div>
              <div>
                {" "}
                Year Net Cost After Year 1: $
                {(this.getMonthlyNetCost() * 12 * 29).toFixed(0)}{" "}
              </div>
              <div>
                {" "}
                Total Cost to Consumer over 30 years: $
                {this.getTotalCostToConsumer()}{" "}
              </div>
              <div>
                {" "}
                Total Monthly Cost to Consumer over 30 years: $
                {(this.getTotalCostToConsumer() / (12 * 30)).toFixed(2)}{" "}
              </div>

              <h4> Oppurtunity Cost </h4>
              <div>
                {" "}
                Annualized Savings: $
                {this.getAnnualizedReturnsSP500ForRoofCost()}{" "}
              </div>
              <div>
                {" "}
                Total Savings: ${this.getTotalReturnsSP500ForRoofCost()}{" "}
              </div>
              <div>
                {" "}
                Net Financing vs Cashout: ${this.getOppurtunityCostToConsumer()}{" "}
              </div>
            </div>

            <div className={styles.optionWrap}>
              {/* <h3> Solar Panel Costs: { formatMoney(this.getCostOfSolarRoof()) }</h3> */}
              {/* financing */}
              <div className={styles.financing}>
                <div
                  className={cx({
                    [styles.table]: true,
                    [styles.tableHeader]: true,
                  })}
                >
                  <div className={styles.row}>
                    <div
                      className={cx({
                        [styles.cell]: true,
                        [styles.notSupported]:
                          this.getCostOfRoof() > this.constants.retail.max,
                      })}
                    >
                      <div>
                        <span className={styles.digits}>
                          <span className={styles.available}>
                            {formatMoney(this.getRetailTotalNetCost())}
                          </span>
                          <span className={styles.null}>—</span>
                        </span>
                        <span className={styles.digitsMonth}>
                          <span className={styles.available}>/ month</span>
                        </span>
                      </div>
                      <h4 className={styles.digitsSubhead}>
                        <span className={styles.available}>
                          average retail loan
                        </span>
                        <span
                          className={cx({
                            [styles.notSupportedAlert]: true,
                            [styles.null]: true,
                          })}
                        >
                          You can only get this size of roof with Better
                        </span>
                      </h4>
                    </div>
                    <div className={styles.cell}>
                      <div className={styles.cost}>
                        <span
                          className={cx({
                            [styles.digits]: true,
                            [styles.green]: true,
                          })}
                        >
                          {formatMoney(this.getSolarTotalNetCost())}
                        </span>
                        <span
                          className={cx({
                            [styles.digitsMonth]: true,
                            [styles.green]: true,
                          })}
                        >
                          / month
                        </span>
                      </div>
                      <h4
                        className={cx({
                          [styles.digitsSubhead]: true,
                          [styles.green]: true,
                        })}
                      >
                        solar mortgage
                      </h4>
                    </div>
                  </div>
                </div>
                <div
                  className={styles.assumptions}
                  data-js-is-assumptions-open={this.state.isAssumptionsOpen}
                >
                  <div
                    className={styles.content}
                    ref={(element) => {
                      this.financingAssumptionsContent = element
                    }}
                  >
                    <div
                      className={cx({
                        [styles.table]: true,
                        [styles.tableFinancing]: true,
                      })}
                    >
                      <div className={styles.row}>
                        <div
                          className={cx({
                            [styles.cell]: true,
                            [styles.labelMd]: true,
                          })}
                        >
                          <span className={styles.labelMd}>Loan cost</span>
                        </div>
                        <div
                          className={cx({
                            [styles.cell]: true,
                            [styles.notSupported]:
                              this.getCostOfRoof() > this.constants.retail.max,
                          })}
                        >
                          <span className={styles.valueMd}>
                            <span className={styles.available}>
                              {formatMoney(-this.getRetailMonthlyPayment())}/mo
                            </span>
                            <span className={styles.null}>—</span>
                          </span>
                        </div>
                        <div className={styles.cell}>
                          <span className={styles.valueMd}>
                            {formatMoney(-this.getSolarMonthlyDifference())}/mo
                          </span>
                        </div>
                      </div>
                      <div className={styles.row}>
                        <div className={styles.cell}>
                          <span className={styles.labelMd}>
                            Value of energy
                          </span>
                        </div>
                        <div
                          className={cx({
                            [styles.cell]: true,
                            [styles.notSupported]:
                              this.getCostOfRoof() > this.constants.retail.max,
                          })}
                        >
                          <span
                            className={cx({
                              [styles.valueMd]: true,
                              [styles.green]: true,
                            })}
                          >
                            <span className={styles.available}>
                              {formatMoney(
                                -this.getAverageMonthlyElectricityIncome()
                              )}
                              /mo
                            </span>
                            <span className={styles.null}>—</span>
                          </span>
                        </div>
                        <div className={styles.cell}>
                          <span
                            className={cx({
                              [styles.valueMd]: true,
                              [styles.green]: true,
                            })}
                          >
                            {formatMoney(
                              -this.getAverageMonthlyElectricityIncome()
                            )}
                            /mo
                          </span>
                        </div>
                      </div>
                      <div className={styles.row}>
                        <div className={styles.cell}>
                          <span className={styles.labelMd}>
                            Tax credit (monthly)
                          </span>
                        </div>
                        <div
                          className={cx({
                            [styles.cell]: true,
                            [styles.notSupported]:
                              this.getCostOfRoof() > this.constants.retail.max,
                          })}
                        >
                          <span
                            className={cx({
                              [styles.valueMd]: true,
                              [styles.green]: true,
                            })}
                          >
                            <span className={styles.available}>
                              {formatMoney(
                                -this.getValueOfSolarTaxCreditOverFullTerm(
                                  5 * 12
                                )
                              )}
                              /mo
                            </span>
                            <span className={styles.null}>—</span>
                          </span>
                        </div>
                        <div className={styles.cell}>
                          <span
                            className={cx({
                              [styles.valueMd]: true,
                              [styles.green]: true,
                            })}
                          >
                            {formatMoney(
                              -this.getValueOfSolarTaxCreditOverFullTerm(
                                30 * 12
                              )
                            )}
                            /mo
                          </span>
                        </div>
                      </div>
                      <div className={styles.row}>
                        <div className={styles.cell}>
                          <span className={styles.labelSm}>Tax credit</span>
                        </div>
                        <div
                          className={cx({
                            [styles.cell]: true,
                            [styles.notSupported]:
                              this.getCostOfRoof() > this.constants.retail.max,
                          })}
                        >
                          <span className={styles.valueSm}>
                            <span className={styles.available}>
                              {formatMoney(
                                -this.constants.taxes.solarTaxCreditRate *
                                  this.getCostOfRoof()
                              )}
                            </span>
                            <span className={styles.null}>—</span>
                          </span>
                        </div>
                        <div className={styles.cell}>
                          <span className={styles.valueSm}>
                            {formatMoney(
                              -this.constants.taxes.solarTaxCreditRate *
                                this.getCostOfRoof()
                            )}
                          </span>
                        </div>
                      </div>
                      <div className={styles.row}>
                        <div className={styles.cell}>
                          <span className={styles.labelSm}>Loan duration</span>
                        </div>
                        <div
                          className={cx({
                            [styles.cell]: true,
                            [styles.notSupported]:
                              this.getCostOfRoof() > this.constants.retail.max,
                          })}
                        >
                          <span className={styles.valueSm}>
                            <span className={styles.available}>
                              5 year term
                            </span>
                            <span className={styles.null}>—</span>
                          </span>
                        </div>
                        <div className={styles.cell}>
                          <span className={styles.valueSm}>30 year term</span>
                        </div>
                      </div>
                      <div className={styles.row}>
                        <div className={styles.cell}>
                          <span className={styles.labelLg}>Net monthly</span>
                        </div>
                        <div
                          className={cx({
                            [styles.cell]: true,
                            [styles.notSupported]:
                              this.getCostOfRoof() > this.constants.retail.max,
                          })}
                        >
                          <span className={styles.valueMd}>
                            <span className={styles.available}>
                              {formatMoney(this.getRetailTotalNetCost())}/mo
                            </span>
                            <span className={styles.null}>—</span>
                          </span>
                        </div>
                        <div className={styles.cell}>
                          <span
                            className={cx({
                              [styles.valueMd]: true,
                              [styles.green]: true,
                            })}
                          >
                            {formatMoney(this.getSolarTotalNetCost())}/mo
                          </span>
                        </div>
                      </div>
                      <div className={styles.row}>
                        <div className={styles.cell}>
                          <span className={styles.labelSm}>Interest rate</span>
                        </div>
                        <div
                          className={cx({
                            [styles.cell]: true,
                            [styles.notSupported]:
                              this.getCostOfRoof() > this.constants.retail.max,
                          })}
                        >
                          <span className={styles.valueSm}>
                            <span className={styles.available}>12%</span>
                            <span className={styles.null}>—</span>
                          </span>
                        </div>
                        <div className={styles.cell}>
                          <span className={styles.valueSm}>4.375%</span>
                        </div>
                      </div>
                      <div className={styles.row}>
                        <div className={styles.cell}>
                          <span className={styles.labelSm}>Loan duration</span>
                        </div>
                        <div
                          className={cx({
                            [styles.cell]: true,
                            [styles.notSupported]:
                              this.getCostOfRoof() > this.constants.retail.max,
                          })}
                        >
                          <span className={styles.valueSm}>
                            <span className={styles.available}>
                              5 year term
                            </span>
                            <span className={styles.null}>—</span>
                          </span>
                        </div>
                        <div className={styles.cell}>
                          <span className={styles.valueSm}>30 year term</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <TeslaToggleAssumptions
                    updateFn={this.toggleAssumptions.bind(this)}
                  />
                </div>
              </div>
              {/* cash */}
              <div className={styles.cash}>
                <div className={styles.cashHeader}>
                  <h2 className={styles.cashHeading}>
                    <div className={styles.cashDigits}>
                      {formatMoney(-this.getOppurtunityCostToConsumer())}
                    </div>
                    <div
                      className={cx({
                        [styles.cashDigitsSubhead]: true,
                        [styles.green]: true,
                      })}
                    >
                      saved
                    </div>
                  </h2>
                </div>
                <div
                  className={styles.assumptions}
                  data-js-is-assumptions-open={this.state.isAssumptionsOpen}
                >
                  <div
                    className={styles.content}
                    ref={(element) => {
                      this.cashAssumptionsContent = element
                    }}
                  >
                    <div
                      className={cx({
                        [styles.table]: true,
                        [styles.tableCash]: true,
                      })}
                    >
                      <div className={styles.row}>
                        <div className={styles.cell}>
                          <span className={styles.labelMd}>Roof savings</span>
                        </div>
                        <div className={styles.cell}>
                          <span
                            className={cx({
                              [styles.valueMd]: true,
                              [styles.green]: true,
                            })}
                          >
                            {formatMoney(
                              -this.getTotalReturnsSP500ForRoofCost()
                            )}
                          </span>
                        </div>
                      </div>
                      <div className={styles.row}>
                        <div className={styles.cell}>
                          <span className={styles.labelMd}>
                            Lifetime cost of Solar Mortgage
                          </span>
                        </div>
                        <div className={styles.cell}>
                          <span
                            className={cx({
                              [styles.valueMd]: true,
                              [styles.green]: true,
                            })}
                          >
                            {formatMoney(this.getTotalCostToConsumer())}
                          </span>
                        </div>
                      </div>
                      <div className={styles.row}>
                        <div className={styles.cell}>
                          <span className={styles.labelMd}>Total</span>
                        </div>
                        <div className={styles.cell}>
                          <span
                            className={cx({
                              [styles.valueMd]: true,
                              [styles.green]: true,
                            })}
                          >
                            {formatMoney(this.getOppurtunityCostToConsumer())}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <TeslaToggleAssumptions
                    updateFn={this.toggleAssumptions.bind(this)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={styles.actions}>
            <Button
              type="primary"
              size="lg"
              halo={true}
              href="https://www.better.com/api/leads?lead[sourceName]=Tesla"
            >
              Start saving now
            </Button>
          </div>
        </div>
      </section>
    )
  }
}
